type NonEmptyArray<T> = [T, ...T[]];

type UniquePageField = 'id' | 'path';

function assertArrayIsNotEmpty<T>(value: T[]): asserts value is NonEmptyArray<T> {
  if (value.length === 0) {
    throw new Error(
      'Encountered an empty array when it was expected that the array have a non-zero length!',
    );
  }
}

const humanizeQuotedList = (values: string[]): string => {
  const formatted = values.map(v => `'${v}'`);
  if (formatted.length === 0) {
    return '';
  }
  if (formatted.length === 1) {
    return formatted[0];
  }
  const head = formatted.slice(0, -1).join(', ');
  const tail = formatted[formatted.length - 1];
  const oxford = formatted.length >= 3 ? ',' : '';
  return `${head}${oxford} and ${tail}`;
};

export class PagesError extends Error {}

/**
 * An error that is thrown when a field, {@link UniquePageField}, is duplicated in the pages
 * configuration array.
 */
export class NonUniquePageConfigurationError extends PagesError {
  constructor(field: UniquePageField, values: NonEmptyArray<string>) {
    assertArrayIsNotEmpty(values);
    let message =
      `Detected a duplicate value, '${values[0]}', for field '${field}', in the ` +
      `pages configuration! The field '${field}' must be unique for all pages!`;
    if (values.length !== 1) {
      const humanized = humanizeQuotedList(values);
      message =
        `Detected duplicate values, ${humanized}, for field '${field}', in the pages ` +
        `configuration! The field '${field}' must be unique for all pages!`;
    }
    super(message);
  }
}

const InvalidPageIdMessageSuffix =
  'in the pages configuration! Page IDs must be in hyphen-case format (i.e. lowercase letters ' +
  'separated by hyphens, with no numbers)!';

export class InvalidPageIdFormatError extends PagesError {
  constructor(ids: NonEmptyArray<string>) {
    assertArrayIsNotEmpty(ids);
    const prefix =
      ids.length === 1
        ? `Detected an invalid page ID, '${ids[0]}',`
        : `Detected invalid page IDs, ${humanizeQuotedList(ids)},`;
    super(`${prefix} ${InvalidPageIdMessageSuffix}`);
  }
}

export class NonUniquePageIdError extends PagesError {
  constructor(id: string, paths: NonEmptyArray<string>) {
    assertArrayIsNotEmpty(paths);
    super(
      `Detected a duplicate page ID, '${id}', for the following paths: ` +
        `${humanizeQuotedList(paths)}! Page IDs ` +
        'must be unique for all pages!',
    );
  }
}

export class MultiplePagesMatchPathError extends PagesError {
  constructor(path: string, pageIds: NonEmptyArray<string>) {
    assertArrayIsNotEmpty(pageIds);
    super(
      `Encountered multiple pages, ${humanizeQuotedList(pageIds)}, that match the provided ` +
        `path '${path}'. This should never happen, and indicates the ` +
        "'matcher' configurations are not correct for at least 1 of the pages.",
    );
  }
}

export class PageForPathNotFoundError extends PagesError {
  constructor(pathname: string) {
    super(`Could not find an associated page for 'pathname': '${pathname}'!`);
  }
}

export class InvalidPageIdError extends PagesError {
  constructor(id: string) {
    super(`The provided page ID, '${id}', is invalid!`);
  }
}

export class InvalidPathPatternError extends PagesError {
  constructor(path: string, message: string) {
    super(`The provided path pattern '${path}' is invalid: ${message}`);
  }
}
